import { useState, useEffect, useRef } from "react";
import { FuelEntry, Option } from "@/types/fuel";
import { normalizeText } from "@/utils/textFormatter";

interface UseFuelFormArgs {
  formEntry: FuelEntry;
  sourceOptions: Option[];
  editing: boolean;
  onFormEntryChange: (entry: FuelEntry) => void;
  onSave: () => void;
}

export const useFuelForm = ({ formEntry, sourceOptions, editing, onFormEntryChange, onSave }: UseFuelFormArgs) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [userIsTyping, setUserIsTyping] = useState(false);
  const [search, setSearch] = useState(
    formEntry.fuelSource ? sourceOptions.find((opt) => opt.value === formEntry.fuelSource)?.label ?? "" : ""
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const [touched, setTouched] = useState({
    fuelSource: false,
    quantity: false,
  });
  const [fuelSourceError, setFuelSourceError] = useState("");
  const [quantityError, setQuantityError] = useState(false);
  const [availableUnits, setAvailableUnits] = useState<string[]>([]);

  // Reset touched state when the modal is opened with a new form entry
  useEffect(() => {
    if (!userIsTyping) {
      setTouched({ fuelSource: false, quantity: false });
      setFuelSourceError("");
      setQuantityError(false);

      if (formEntry.fuelSource) {
        if (editing) {
          const matchingOption = sourceOptions.find((opt) => opt.value === formEntry.fuelSource);
          setSearch(matchingOption?.label || formEntry.fuelSource); // Use the value itself as fallback
        } else {
          const matchingOption = sourceOptions.find((opt) => opt.value === formEntry.fuelSource);
          if (matchingOption) {
            setSearch(matchingOption.label);
          }
        }
      } else {
        setSearch("");
      }
    }
  }, [formEntry.fuelSource, sourceOptions, editing, userIsTyping]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filter options based on search and editing state
  const filteredOptions = sourceOptions.filter(
    (option) => {
      const matchesSearch = normalizeText(option.label).includes(normalizeText(search)) || !search;
      const notAlreadySelected = !editing || option.value !== formEntry.fuelSource;
      return matchesSearch && notAlreadySelected;
    }
  ).sort((a, b) => a.label.localeCompare(b.label));

  /** Handle fuel source search input change */
  const handleSourceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserIsTyping(true);
    setSearch(e.target.value);
    setShowDropdown(true);

    if (!e.target.value) {
      onFormEntryChange({
        ...formEntry,
        fuelSource: "",
      });
      setAvailableUnits([]);
    }
  };

  /** Handle fuel source selection */
  const handleSourceSelect = (option: Option, e: React.MouseEvent) => {
    e.preventDefault();
    setShowDropdown(false);
    setSearch(option.label);
    setUserIsTyping(false);

    // When selecting a new source, we need to get available units from the db
    // This is handled in the FuelsStep component and passed through props

    onFormEntryChange({
      ...formEntry,
      fuelSource: option.value,
    });
    setTouched({ ...touched, fuelSource: true });
    setFuelSourceError("");
  };

  /** Handle input click to show dropdown */
  const handleInputClick = () => {
    setShowDropdown(!showDropdown);
  };

  /** Handle fuel source blur */
  const handleSourceBlur = () => {
    setTouched({ ...touched, fuelSource: true });

    setTimeout(() => {
      if (!dropdownRef.current?.contains(document.activeElement)) {
        setShowDropdown(false);
        setUserIsTyping(false);
      }
    }, 200);
  };

  /** Handle quantity change */
  const handleQuantityChange = (value: string) => {
    onFormEntryChange({
      ...formEntry,
      quantity: value === "" ? "" : parseFloat(value),
    });
    setTouched({ ...touched, quantity: true });
    setQuantityError(false);
  };

  /** Make sure quantity is a valid number on blur */
  const handleQuantityBlur = () => {
    setTouched({ ...touched, quantity: true });

    if (formEntry.quantity === "") {
      setQuantityError(true);
      return;
    }

    const value = Number(formEntry.quantity);
    if (isNaN(value) || value <= 0) {
      setQuantityError(true);
      return;
    }

    setQuantityError(false);
  };

  /** Handle unit change when multiple units are available */
  const handleUnitChange = (unit: string) => {
    onFormEntryChange({
      ...formEntry,
      unit: unit,
    });
  };

  /** Set available units for the selected fuel source */
  const setUnitsForFuelSource = (units: string[]) => {
    setAvailableUnits(units);

    // If we have units and no unit is selected, default to the first one
    if (units.length > 0 && (!formEntry.unit || !units.includes(formEntry.unit))) {
      onFormEntryChange({
        ...formEntry,
        unit: units[0],
      });
    }
  };

  /** Validate form before save */
  const handleSaveClick = (t: (key: string, ...args: any[]) => string) => {
    setTouched({ fuelSource: true, quantity: true });

    let valid = true;

    // Fuel source validation
    if (!formEntry.fuelSource) {
      setFuelSourceError(t("validation.required"));
      valid = false;
    }

    // Quantity validation
    if (formEntry.quantity === "" || Number(formEntry.quantity) <= 0) {
      setQuantityError(true);
      valid = false;
    }

    if (valid) {
      onSave();
    }
  };

  // Check if form is valid for enabling/disabling save button
  const isFormValid = (): boolean => {
    if (!formEntry.fuelSource) return false;
    if (formEntry.quantity === "" || Number(formEntry.quantity) <= 0) return false;
    return true;
  };

  return {
    dropdownRef,
    search,
    showDropdown,
    fuelSourceError,
    quantityError,
    touched,
    filteredOptions,
    availableUnits,
    isFormValid,
    handleSourceChange,
    handleSourceSelect,
    handleInputClick,
    handleSourceBlur,
    handleQuantityChange,
    handleQuantityBlur,
    handleUnitChange,
    setUnitsForFuelSource,
    handleSaveClick,
  };
};
